import { ChartDataset } from 'chart.js';
import { useMemo } from 'react';
import { useAppSelector } from '../../../app/hooks';
import {
  BlueberryColorsMap,
  BlueberryFeatures,
  CubeDimensionEnum,
  FeatureEnum,
} from '../../../services/data/impl/cube/cubeSlice';
import {
  findHighestModelVersion,
  isBlueberryFeature,
} from '../../../services/data/impl/cube/cubeUtils';
import { isFeatureEnum } from '../../commonUtils';
import useCurrServicesAndMeasures from '../../customerSummary/hooks/useCurrServicesAndMeasures';
import { selectScanDataTableExists } from '../../duckDB/duckDBSlice';
import { DuckQueryReturnListType } from '../../duckDB/hooks/useDuckQueryScanData';
import { selectCurrFeature } from '../../select/selectSlice';
import { useUnits } from '../../settings/hooks/useUnits';
import { ScanDataEnum } from '../scanDataDefinitions';
import { blueberrySortOrder } from '../scanDataUtils';
import { useBlueberryFlowerFeatures } from './useBlueberryFlowerFeatures';
import { useGetIntervalAndMeasure } from './useGetIntervalAndMeasure';
import { OperatorEnum, useScanDataQuery } from './useScanDataQuery';

interface QueryReturnType {
  class_name: string;
  sum: number;
}

const group = [ScanDataEnum.class_name];

/**
 * Returns blueberry class distribution options for bar chart
 * @returns {DuckQueryReturnListType<ChartDataset<'bar', number[]>}
 */
export const useBlueberryDistribution = (): DuckQueryReturnListType<
  ChartDataset<'bar', number[]>
> => {
  const units = useUnits();
  const currFeature = useAppSelector(selectCurrFeature);
  const { measure } = useGetIntervalAndMeasure();
  const scanDataTableExists = useAppSelector(selectScanDataTableExists);
  const { servicesAndMeasuresWithFeatureFilter } = useCurrServicesAndMeasures();

  const flowerFeatures = useBlueberryFlowerFeatures();

  // For "blueberry", only model version and name are needed. Class color is arbitrary (e.g., blueberry_Blue). Model versions are synced.
  const latestService = findHighestModelVersion(
    servicesAndMeasuresWithFeatureFilter.availableServicesRaw,
    [FeatureEnum.blueberry_Blue]
  );

  const filters = useMemo(() => {
    const newFilters = [];

    if (latestService) {
      newFilters.push(
        {
          col: ScanDataEnum.model_name,
          op: OperatorEnum.eq,
          value: latestService[CubeDimensionEnum.modelName],
        },
        {
          col: ScanDataEnum.model_version,
          op: OperatorEnum.eq,
          value: latestService[CubeDimensionEnum.modelVersion],
        },
        {
          col: ScanDataEnum.class_name,
          op: OperatorEnum.in,
          value: [...BlueberryFeatures, ...flowerFeatures],
        }
      );
    }

    return newFilters;
  }, [latestService]);

  // Sum up all counts for each class (green, yellow, purple, blue)
  const cols = useMemo(() => {
    return [
      ScanDataEnum.class_name,
      `SUM(${ScanDataEnum.estimated_count}) as sum`,
    ];
  }, [measure, units]);

  const scanDistributionFilteredByFeature = useScanDataQuery<QueryReturnType>({
    cols,
    group,
    filters,
    skip:
      !latestService ||
      servicesAndMeasuresWithFeatureFilter.availableServicesRaw.length === 0 ||
      !isBlueberryFeature(currFeature) ||
      !scanDataTableExists,
    disableClassFilter: true,
    useUnitsTransformSubstringMatching: true,
  });

  // Create datasets in chart.js bar chart format and sort by ripeness
  const datasets: ChartDataset<'bar', number[]>[] = useMemo(() => {
    const datasets: ChartDataset<'bar', number[]>[] = [];

    // generate datasets for chart.js bar chart
    if (scanDistributionFilteredByFeature.data) {
      scanDistributionFilteredByFeature.data.forEach((el) => {
        datasets.push({
          label: el.class_name,
          backgroundColor: BlueberryColorsMap[el.class_name],
          data: [el.sum],
        });
      });
    }

    // Sort by ripeness from green, to pink (yellow), to purple, to blue
    if (datasets.length > 1) {
      datasets.sort((a, b) => {
        const aClass = a.label;
        const bClass = b.label;

        if (
          aClass &&
          isFeatureEnum(aClass) &&
          bClass &&
          isFeatureEnum(bClass)
        ) {
          return (
            blueberrySortOrder.indexOf(aClass as FeatureEnum) -
            blueberrySortOrder.indexOf(bClass as FeatureEnum)
          );
        } else {
          console.log('Invalid class name detected: ', aClass, bClass);
          return 0;
        }
      });
    }

    return datasets;
  }, [scanDistributionFilteredByFeature.data]);

  return {
    ...scanDistributionFilteredByFeature,
    data: scanDistributionFilteredByFeature.data ? datasets : null,
  };
};
